import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Exercise } from "../../types";
import { sendChat } from "../../utils/api";
import { Banner, Icon } from "../common/ui";
import Lightbox from "../common/Lightbox";

interface Props {
  exercise: Exercise;
  token: string;
  isRu: boolean;
  /** Free lessons are a demo — the task is shown, but work goes in for review
   *  only on a subscription (the server enforces it too). */
  canSubmit?: boolean;
  /** The lesson's paired action buttons already carry this task's send button;
   *  showing it here too would offer the same action twice. */
  hideSubmit?: boolean;
  onOpenPlans?: () => void;
  /** false → the lesson shows its own access card, so stay silent here. */
  explainLock?: boolean;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ExercisePanel({ exercise, token, isRu, canSubmit = true, hideSubmit = false, onOpenPlans, explainLock = true,
}: Props) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);

  const title = isRu ? exercise.title_ru : (exercise.title_en || exercise.title_ru);
  const instructions = isRu ? exercise.instructions_ru : (exercise.instructions_en || exercise.instructions_ru);
  const whatToSubmit = isRu ? exercise.what_to_submit_ru : (exercise.what_to_submit_en || exercise.what_to_submit_ru);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f)); setError(null); setSent(false);
  };

  const submit = async () => {
    if (!file) return;
    setSending(true); setError(null);
    try {
      const photo = await fileToDataUrl(file);
      await sendChat(token, { photo_base64: photo, exercise_id: exercise.id });
      setFile(null); setPreview(null); setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass glass-sm" style={{ padding: 16, background: "var(--glass-1)" }}>
      <div className="row" style={{ gap: 8, marginBottom: 10 }}>
        <span style={{
          display: "grid", placeItems: "center", width: 28, height: 28, borderRadius: 8,
          background: "var(--violet-tint)", color: "var(--violet-text)", flex: "none",
        }}>
          <Icon name="brush" size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="eyebrow" style={{ marginBottom: 2, color: "var(--violet-text)" }}>{t("exercise.heading")}</p>
          <p className="t-h" style={{ margin: 0 }}>{title}</p>
        </div>
      </div>

      <p className="t-sm muted" style={{ fontWeight: 500, lineHeight: 1.55, marginBottom: 12, whiteSpace: "pre-wrap" }}>
        {instructions}
      </p>

      <div style={{ padding: "10px 12px", borderRadius: "var(--r-sm)", background: "var(--violet-tint)", marginBottom: 12 }}>
        <p className="eyebrow" style={{ marginBottom: 4, color: "var(--violet-text)" }}>{t("exercise.whatToSubmit")}</p>
        <p className="t-sm" style={{ margin: 0, fontWeight: 600, lineHeight: 1.45 }}>{whatToSubmit}</p>
      </div>

      {/* What "done" looks like — the last thing read before sending work in. */}
      {exercise.example_image && (
        <figure style={{ margin: "0 0 12px" }}>
          <img
            src={exercise.example_image}
            alt={t("exercise.example")}
            loading="lazy"
            onClick={() => setZoom(true)}
            style={{
              width: "100%", height: "auto", display: "block", borderRadius: "var(--r-sm)",
              border: "1px solid var(--border)", background: "var(--glass-1)", cursor: "zoom-in",
            }}
          />
          <figcaption className="t-xs muted-2" style={{ marginTop: 6, textAlign: "center" }}>
            {t("exercise.example")}
          </figcaption>
        </figure>
      )}
      {zoom && exercise.example_image && (
        <Lightbox src={exercise.example_image} onClose={() => setZoom(false)} />
      )}

      {preview && (
        <div style={{ marginBottom: 10, borderRadius: "var(--r-sm)", overflow: "hidden", position: "relative" }}>
          <img src={preview} alt="preview" style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block" }} />
          <button onClick={() => { setFile(null); setPreview(null); }} className="icon-btn"
            style={{ position: "absolute", top: 8, right: 8, width: 32, height: 32, borderRadius: 8 }}>
            <Icon name="x" size={16} />
          </button>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" onChange={pick} style={{ display: "none" }} />

      {(hideSubmit && canSubmit) || (!canSubmit && !explainLock) ? null : !canSubmit ? (
        <div style={{ padding: "12px 14px", borderRadius: "var(--r-sm)", background: "var(--glass-1)", border: "1px dashed var(--border)" }}>
          <p className="t-sm" style={{ margin: 0, fontWeight: 700, display: "flex", alignItems: "center", gap: 7 }}>
            <Icon name="lock" size={15} /> {t("exercise.subscriptionToSubmit")}
          </p>
          <p className="t-xs muted-2" style={{ margin: "5px 0 0", lineHeight: 1.45 }}>
            {t("exercise.subscriptionToSubmitHint")}
          </p>
          {onOpenPlans && (
            <button className="btn btn-violet btn-block" onClick={onOpenPlans} style={{ marginTop: 11 }}>
              <Icon name="sparkles" size={17} /> {t("exercise.openPlans")}
            </button>
          )}
        </div>
      ) : !preview ? (
        <button className="btn btn-glass btn-block" onClick={() => fileRef.current?.click()}>
          <Icon name="image" size={18} /> {t("exercise.sendWork")}
        </button>
      ) : (
        <button className="btn btn-violet btn-block" onClick={submit} disabled={sending}>
          {sending ? <span className="spinner" /> : <><Icon name="send" size={17} /> {t("exercise.sendWork")}</>}
        </button>
      )}

      {error && <div style={{ marginTop: 10 }}><Banner kind="error">{error}</Banner></div>}
      {sent && <div style={{ marginTop: 10 }}><Banner kind="success">{t("exercise.workSent")}</Banner></div>}

      {canSubmit && !hideSubmit && (
        <p className="t-xs muted-2" style={{ marginTop: 10, textAlign: "center" }}>
          <Icon name="message" size={12} /> {t("exercise.discussInChat")}
        </p>
      )}
    </div>
  );
}
